import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import CrudPage from "../components/CrudPage";
import { naira } from "../lib/format";
import { Page, PageHeader } from "../components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

function Categories() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("");
  const [editRow, setEditRow] = useState(null);

  const load = () => api.get("/categories").then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error("Category name is required");
    try { await api.post("/categories", { name: name.trim(), icon }); toast.success("Category created"); setName(""); setIcon(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    try { await api.put(`/categories/${editRow.id}`, { name: editRow.name, icon: editRow.icon || "" }); toast.success("Category updated"); setEditRow(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (r) => {
    try { await api.del(`/categories/${r.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="menu-categories">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold" style={{ color: "#765A4A" }}>New category</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Grills & Suya" data-testid="cat-name" />
          </div>
          <div className="w-28">
            <label className="text-xs font-semibold" style={{ color: "#765A4A" }}>Icon</label>
            <Input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🍢" data-testid="cat-icon" />
          </div>
          <Button onClick={create} data-testid="cat-create" className="text-white" style={{ background: "#8B1E1E" }}>
            <Plus size={16} className="mr-1" /> Add category
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr style={{ background: "#FBF6EE", color: "#765A4A" }}>
            <th className="text-left px-5 py-3">Category</th><th className="text-left px-4 py-3">Icon</th><th className="px-4 py-3" /></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b" style={{ borderColor: "#F5EFE5" }} data-testid={`cat-row-${i}`}>
                <td className="px-5 py-3">
                  {editRow?.id === r.id
                    ? <Input value={editRow.name} onChange={(e) => setEditRow({ ...editRow, name: e.target.value })} data-testid="cat-edit-name" />
                    : <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span>}
                </td>
                <td className="px-4 py-3">
                  {editRow?.id === r.id
                    ? <Input value={editRow.icon || ""} onChange={(e) => setEditRow({ ...editRow, icon: e.target.value })} className="w-20" />
                    : (r.icon || "—")}
                </td>
                <td className="px-4 py-3 text-right">
                  {editRow?.id === r.id ? (
                    <span className="flex justify-end gap-1">
                      <button onClick={saveEdit} data-testid="cat-edit-save" className="p-1.5 rounded-lg" style={{ color: "#166534" }}><Check size={15} /></button>
                      <button onClick={() => setEditRow(null)} className="p-1.5 rounded-lg" style={{ color: "#765A4A" }}><X size={15} /></button>
                    </span>
                  ) : (
                    <span className="flex justify-end gap-1">
                      <button onClick={() => setEditRow(r)} data-testid={`cat-edit-${r.id}`} className="p-1.5 rounded-lg" style={{ color: "#8B1E1E" }}><Pencil size={15} /></button>
                      <button onClick={() => remove(r)} data-testid={`cat-delete-${r.id}`} className="p-1.5 rounded-lg" style={{ color: "#A52222" }}><Trash2 size={15} /></button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-5 py-10 text-center" style={{ color: "#a58f7c" }}>No categories yet</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function Menu() {
  const [lookups, setLookups] = useState({ categories: [], modifier_groups: [] });
  useEffect(() => {
    Promise.all([api.get("/categories"), api.get("/modifier-groups").catch(() => [])])
      .then(([categories, modifier_groups]) => setLookups({ categories, modifier_groups }))
      .catch(() => {});
  }, []);

  const columns = [
    { key: "name", label: "Item", render: (r) => (
      <span className="flex items-center gap-2">
        {r.image ? <img src={r.image} alt="" className="h-8 w-8 rounded object-cover" /> : <span className="h-8 w-8 rounded" style={{ background: "#F3ECE2" }} />}
        <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span>
      </span>) },
    { key: "category_name", label: "Category" },
    { key: "product_type", label: "Type", render: (r) => <span className="text-xs capitalize px-2 py-0.5 rounded" style={{ background: "#F3ECE2", color: "#765A4A" }}>{r.product_type}</span> },
    { key: "cost", label: "Cost", align: "right", render: (r) => naira(r.cost) },
    { key: "price", label: "Price", align: "right", render: (r) => (
      <span className="font-semibold" style={{ color: "#8B1E1E" }}>{naira(r.price)}<span className="font-normal text-[11px]" style={{ color: "#a58f7c" }}> /{r.price_unit || "portion"}</span></span>) },
    { key: "margin", label: "Margin", align: "right", render: (r) => r.price ? `${Math.round((r.price - r.cost) / r.price * 100)}%` : "—" },
    { key: "station", label: "Station" },
  ];

  const fields = [
    { name: "name", label: "Item Name", required: true, full: true },
    { name: "category_id", label: "Category", type: "select", optionsFrom: "categories" },
    { name: "product_type", label: "Type", type: "select", default: "finished", options: [{ value: "finished", label: "Finished (recipe)" }, { value: "stock", label: "Stock item" }, { value: "raw", label: "Raw ingredient" }, { value: "combo", label: "Combo" }] },
    { name: "cost", label: "Cost (₦)", type: "number", default: 0 },
    { name: "price", label: "Price (₦)", type: "number", default: 0 },
    { name: "price_unit", label: "Price is per", type: "select", default: "portion", options: [{ value: "portion", label: "Portion" }, { value: "plate", label: "Plate" }, { value: "piece", label: "Piece" }, { value: "scoop", label: "Scoop" }, { value: "bottle", label: "Bottle" }, { value: "kg", label: "Kilogram" }, { value: "litre", label: "Litre" }] },
    { name: "tax_rate", label: "Tax %", type: "number", default: 7.5 },
    { name: "station", label: "KDS Station", type: "select", default: "kitchen", options: [{ value: "kitchen", label: "Kitchen" }, { value: "gelato", label: "Gelato" }, { value: "bakery", label: "Bakery" }, { value: "bar", label: "Bar" }, { value: "none", label: "None" }] },
    { name: "image", label: "Item photo", type: "image", full: true },
    { name: "modifier_group_ids", label: "Add-on groups for this item", type: "multiselect", optionsFrom: "modifier_groups", full: true, emptyText: "No add-on groups configured yet — create them under Settings › Modifier groups" },
    { name: "description", label: "Description", type: "textarea", full: true },
  ];

  return (
    <Page>
      <PageHeader title="Menu" subtitle="Items, photos, add-ons, pricing & categories" />
      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="menu-tab-items">Menu items</TabsTrigger>
          <TabsTrigger value="categories" data-testid="menu-tab-categories">Categories</TabsTrigger>
        </TabsList>
        <TabsContent value="items" className="mt-4">
          <CrudPage title="Menu" subtitle="" module="menu" endpoint="/products" columns={columns} fields={fields}
            lookups={lookups}
            beforeSave={(p) => ({ ...p, station: p.station === "none" ? "" : p.station, modifier_group_ids: p.modifier_group_ids || [] })}
            searchKeys={["name", "category_name", "sku"]} testId="menu" addLabel="New Item"
            defaults={{ product_type: "finished", tax_rate: 7.5, station: "kitchen", price_unit: "portion", modifier_group_ids: [] }} />
        </TabsContent>
        <TabsContent value="categories" className="mt-4"><Categories /></TabsContent>
      </Tabs>
    </Page>
  );
}
